import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, Mail, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_beverage-innovation/artifacts/thcgu0ac_UI%20Letter%20Head.png";

export default function Footer() {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer data-testid="footer" className="bg-[#0B1D3A] relative overflow-hidden">
      {/* CTA Banner */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="flex flex-col lg:flex-row items-center justify-between gap-8"
          >
            <div>
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-2">
                Ready to Transform Your Beverage Business?
              </h3>
              <p className="text-slate-400 text-base">
                Join India's fastest-growing frozen and carbonated beverage network.
              </p>
            </div>
            <Button
              onClick={() => scrollTo("#contact")}
              className="rounded-full bg-[#C8D616] text-[#0B1D3A] hover:bg-[#B5C314] px-8 py-6 text-base font-bold shrink-0"
            >
              Get Started
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <img src={LOGO_URL} alt="Ultimate Infusions" className="h-11 w-auto rounded-lg" />
              <div className="flex flex-col gap-0.5 w-[90px] text-white" style={{ fontFamily: "'Controller W01 Three', sans-serif" }}>
                <span className="flex justify-between text-base leading-none">
                  {'ULTIMATE'.split('').map((c, i) => <span key={i}>{c}</span>)}
                </span>
                <span className="flex justify-between text-base leading-none text-[#C8D616]">
                  {'INFUSIONS'.split('').map((c, i) => <span key={i}>{c}</span>)}
                </span>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6 max-w-xs">
              Next-generation frozen and carbonated beverage solutions for India,
              backed by 40+ years of Australian expertise.
            </p>
            {/* Contact inline */}
            <div className="flex flex-col gap-3">
              <a href="tel:+919110725125" className="flex items-center gap-2.5 text-sm text-slate-400 hover:text-[#C8D616] transition-colors">
                <Phone size={14} className="shrink-0" />
                +91 9110725125
              </a>
              <a href="mailto:info@ultimateinfusions.com" className="flex items-center gap-2.5 text-sm text-slate-400 hover:text-[#C8D616] transition-colors">
                <Mail size={14} className="shrink-0" />
                info@ultimateinfusions.com
              </a>
              <span className="flex items-center gap-2.5 text-sm text-slate-400">
                <MapPin size={14} className="shrink-0" />
                Hyderabad, Telangana
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2 lg:col-start-6">
            <h4 className="text-xs font-bold text-white uppercase tracking-widest mb-5">Company</h4>
            <ul className="space-y-3">
              {[
                { label: "About Us", href: "#about" },
                { label: "Products", href: "#products" },
                { label: "Business Model", href: "#business-model" },
                { label: "Industries", href: "#industries" },
              ].map((link) => (
                <li key={link.label}>
                  <button onClick={() => scrollTo(link.href)} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-xs font-bold text-white uppercase tracking-widest mb-5">Solutions</h4>
            <ul className="space-y-3">
              {[
                { label: "Frozen Beverage", href: "#products" },
                { label: "Slush Beverage", href: "#products" },
                { label: "Post Mix Beverage", href: "#products" },
                { label: "Why Choose Us", href: "#why-us" },
              ].map((link) => (
                <li key={link.label}>
                  <button onClick={() => scrollTo(link.href)} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-xs font-bold text-white uppercase tracking-widest mb-5">Connect</h4>
            <ul className="flex flex-col gap-3">
              <li>
                <button onClick={() => scrollTo("#partner")} className="text-sm text-slate-400 hover:text-white transition-colors">
                  Partner With Us
                </button>
              </li>
              <li>
                <button onClick={() => scrollTo("#contact")} className="text-sm text-slate-400 hover:text-white transition-colors">
                  Contact Us
                </button>
              </li>
              <li>
                <a
                  href="https://wa.me/919110725125"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-slate-400 hover:text-[#25D366] transition-colors"
                >
                  WhatsApp
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} Ultimate Infusions Private Limited. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="text-xs text-slate-500 hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="text-xs text-slate-500 hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
